import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpCircle } from 'lucide-react';
import { apiFetch } from '@/shared/api/client';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { APP_VERSION } from '@/shared/constants';
import { AVISADA_KEY, hayQueAvisar, parseAvisada, type Avisada } from './nuevaVersion.logic';

// sessionStorage can be missing or throw (private mode, blocked storage): every access is guarded.
const leerAvisada = () => {
  try {
    return parseAvisada(sessionStorage.getItem(AVISADA_KEY));
  } catch {
    return null;
  }
};

/**
 * "Hay una versión nueva" popup. The production version is published by
 * .github/workflows/app-release.yml only on Production (main) deploys, so develop builds never
 * trigger it. Checked on mount, on window focus and every 5 minutes while the tab is visible.
 */
export function NuevaVersionDialog() {
  // The silence also lives in memory: without sessionStorage, "Más tarde" must still close the popup.
  const [enMemoria, setEnMemoria] = useState<Avisada | null>(null);
  const { data } = useQuery({
    queryKey: ['app-version'],
    queryFn: () => apiFetch<{ version: string | null }>('/api/app-version'),
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const desplegada = data?.version ?? null;
  const nuevaVersion = hayQueAvisar(desplegada, APP_VERSION, leerAvisada() ?? enMemoria) ? desplegada : null;

  /** Closes the popup and silences that version for 10 minutes (see nuevaVersion.logic.ts). */
  const descartar = () => {
    if (!nuevaVersion) return;
    const avisada = { version: nuevaVersion, ts: Date.now() };
    try {
      sessionStorage.setItem(AVISADA_KEY, JSON.stringify(avisada));
    } catch {
      /* stays in memory only */
    }
    setEnMemoria(avisada);
  };

  return (
    <Dialog open={Boolean(nuevaVersion)} onOpenChange={(abierto) => !abierto && descartar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-primary" aria-hidden />
            Hay una versión nueva
          </DialogTitle>
          <DialogDescription>Se publicó la versión {nuevaVersion}. Actualizá para seguir trabajando con la última.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={descartar}>
            Más tarde
          </Button>
          <Button
            onClick={() => {
              descartar();
              window.location.reload();
            }}
          >
            Actualizar ahora
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
